/**
 * ActivityStateMachine - Universal state machine for all activity types.
 *
 * Generalizes ChallengeStateMachine from bumblebee experiment to support
 * multiple activity types with type-specific state transitions.
 *
 * @see https://github.com/micoverde/scratchjr-bumblebee-experiment
 * @see GitHub Issue #852
 */

// Universal states shared by all activity types
const STATES = Object.freeze({
    LOADING: 'LOADING',
    READY: 'READY',
    ACTIVE: 'ACTIVE',
    EVALUATING: 'EVALUATING',
    SUCCESS: 'SUCCESS',
    NEEDS_WORK: 'NEEDS_WORK'
});

// Extended states for Guided Creation activities
const GUIDED_STATES = Object.freeze({
    GUIDED_STEP: 'GUIDED_STEP',
    FREE_PLAY: 'FREE_PLAY'
});

// Extended states for Challenge activities (ported from bumblebee)
const CHALLENGE_STATES = Object.freeze({
    BUILDING: 'BUILDING',
    RUNNING: 'RUNNING',
    VALIDATING: 'VALIDATING',
    FAILED: 'FAILED'
});

// All states combined
const ALL_STATES = Object.freeze({
    ...STATES,
    ...GUIDED_STATES,
    ...CHALLENGE_STATES
});

// Transition maps per activity type
const TRANSITIONS = Object.freeze({
    // Universal fallback transitions
    universal: {
        LOADING: ['READY'],
        READY: ['ACTIVE'],
        ACTIVE: ['EVALUATING'],
        EVALUATING: ['SUCCESS', 'NEEDS_WORK'],
        SUCCESS: ['READY', 'LOADING'],
        NEEDS_WORK: ['ACTIVE', 'READY', 'LOADING']
    },

    // Challenge type (ported from bumblebee ChallengeStateMachine)
    challenge: {
        LOADING: ['READY'],
        READY: ['BUILDING'],
        BUILDING: ['RUNNING', 'READY', 'SUCCESS'],
        RUNNING: ['VALIDATING', 'BUILDING'],
        VALIDATING: ['SUCCESS', 'FAILED', 'BUILDING'],
        SUCCESS: ['READY', 'LOADING', 'BUILDING'],
        FAILED: ['BUILDING', 'READY', 'LOADING']
    },

    // Guided Creation type (new for exemplar activity)
    'guided-creation': {
        LOADING: ['READY'],
        READY: ['GUIDED_STEP'],
        GUIDED_STEP: ['GUIDED_STEP', 'FREE_PLAY', 'READY'],
        FREE_PLAY: ['EVALUATING', 'GUIDED_STEP'],
        EVALUATING: ['SUCCESS', 'NEEDS_WORK'],
        SUCCESS: ['READY', 'LOADING'],
        NEEDS_WORK: ['GUIDED_STEP', 'FREE_PLAY', 'READY']
    },

    // Free Create type (Phase 2)
    'free-create': {
        LOADING: ['READY'],
        READY: ['ACTIVE'],
        ACTIVE: ['EVALUATING'],
        EVALUATING: ['SUCCESS', 'NEEDS_WORK'],
        SUCCESS: ['READY', 'LOADING'],
        NEEDS_WORK: ['ACTIVE', 'READY']
    },

    // Explore type (Phase 2)
    explore: {
        LOADING: ['READY'],
        READY: ['ACTIVE'],
        ACTIVE: ['EVALUATING', 'ACTIVE'],
        EVALUATING: ['SUCCESS', 'NEEDS_WORK'],
        SUCCESS: ['READY', 'LOADING'],
        NEEDS_WORK: ['ACTIVE', 'READY']
    }
});

export default class ActivityStateMachine {
    /**
     * @param {string} activityType - One of: 'challenge', 'guided-creation', 'free-create', 'explore'
     */
    constructor (activityType) {
        const transitionMap = TRANSITIONS[activityType] || TRANSITIONS.universal;
        this._transitions = transitionMap;
        this._activityType = activityType;
        this._state = ALL_STATES.LOADING;
        this._listeners = [];
        this._stepIndex = 0; // For guided-creation: current step number
    }

    get state () {
        return this._state;
    }

    get activityType () {
        return this._activityType;
    }

    get stepIndex () {
        return this._stepIndex;
    }

    /**
     * Attempt a state transition.
     * @param {string} newState - Target state
     * @param {object} [meta] - Optional metadata (e.g., stepIndex for guided steps)
     * @returns {boolean} Whether the transition succeeded
     */
    transition (newState, meta) {
        const allowed = this._transitions[this._state];
        if (!allowed || !allowed.includes(newState)) {
            console.warn(
                `[ActivityStateMachine] Invalid transition: ${this._state} → ${newState} ` +
                `(type: ${this._activityType}, allowed: [${(allowed || []).join(', ')}])`
            );
            return false;
        }

        const prev = this._state;
        this._state = newState;

        // Track step index for guided creation
        if (meta && typeof meta.stepIndex === 'number') {
            this._stepIndex = meta.stepIndex;
        }

        this._notify(prev, newState, meta);
        return true;
    }

    /**
     * Register a state change listener.
     * @param {function(string, string, object): void} fn - Callback(prevState, newState, meta)
     */
    onStateChange (fn) {
        if (typeof fn === 'function') {
            this._listeners.push(fn);
        }
    }

    /**
     * Remove a listener.
     * @param {function} fn
     */
    offStateChange (fn) {
        this._listeners = this._listeners.filter(l => l !== fn);
    }

    /**
     * Reset to LOADING (only from terminal states or READY).
     * @returns {boolean}
     */
    reset () {
        const terminalStates = ['SUCCESS', 'NEEDS_WORK', 'FAILED', 'READY'];
        if (!terminalStates.includes(this._state)) {
            console.warn(`[ActivityStateMachine] Cannot reset from ${this._state}`);
            return false;
        }
        const prev = this._state;
        this._state = ALL_STATES.LOADING;
        this._stepIndex = 0;
        this._notify(prev, ALL_STATES.LOADING, {reset: true});
        return true;
    }

    /**
     * Check if a transition is valid without performing it.
     * @param {string} newState
     * @returns {boolean}
     */
    canTransition (newState) {
        const allowed = this._transitions[this._state];
        return allowed ? allowed.includes(newState) : false;
    }

    /**
     * Get all valid transitions from current state.
     * @returns {string[]}
     */
    validTransitions () {
        return this._transitions[this._state] || [];
    }

    /** @returns {boolean} */
    isTerminal () {
        return ['SUCCESS', 'NEEDS_WORK', 'FAILED'].includes(this._state);
    }

    destroy () {
        this._listeners = [];
    }

    _notify (prev, next, meta) {
        for (const fn of this._listeners) {
            try {
                fn(prev, next, meta || {});
            } catch (err) {
                console.error('[ActivityStateMachine] Listener error:', err);
            }
        }
    }
}

export {STATES, GUIDED_STATES, CHALLENGE_STATES, ALL_STATES, TRANSITIONS};
